import { useEffect, useState } from "react";

import ConfirmPanel from "../components/ConfirmPanel";
import ScopeGate from "../components/ScopeGate";
import {
  Field,
  JsonTextArea,
  SaveBar,
  SectionCard,
  Select,
  TextInput,
  Toggle,
  type SelectOption,
} from "../components/form";
import { useEditableSection } from "../hooks/useEditableSection";
import {
  deleteRealmEmbedding,
  fetchLlmCatalog,
  fetchRealmEmbedding,
  fetchRealmLlmBinding,
  setRealmEmbedding,
  setRealmLlmBinding,
} from "../lib/api";
import { describeApiError } from "../lib/apiError";
import { parseStringMap } from "../lib/jsonBundle";
import { canAdminRealm, canByok } from "../lib/scopes";
import { useGrants } from "../lib/session";
import type { CatalogProvider, EmbeddingBinding, LlmBinding } from "../lib/types";
import { useRealmContext } from "./RealmLayout";

function providerOptions(catalog: CatalogProvider[]): SelectOption[] {
  return catalog.map((p) => ({
    value: p.name,
    label: p.enabled ? p.name : `${p.name} (disabled)`,
    disabled: !p.enabled,
    hint: [
      p.type,
      p.default_completion_model ? `default: ${p.default_completion_model}` : null,
      p.needs_byok ? "no deployment credential — needs BYOK" : null,
    ]
      .filter(Boolean)
      .join(" · "),
  }));
}

/** Empty string ⇒ omit the field; a whitespace-only entry is not a credential. */
function credentialsOrUndefined(raw: string): Record<string, string> | undefined {
  return raw.trim() ? parseStringMap(raw) : undefined;
}

interface LlmDraft {
  providerName: string;
  completionModel: string;
  inheritOrgCredentials: boolean;
  /** Raw JSON; platform-admin only. Empty ⇒ catalog reference, not "clear". */
  credentials: string;
}

function LlmSection({ realmId, catalog }: { realmId: string; catalog: CatalogProvider[] }) {
  const grants = useGrants();
  const byokAllowed = canByok(grants);

  const s = useEditableSection<LlmBinding | null, LlmDraft>({
    load: () => fetchRealmLlmBinding(realmId),
    toDraft: (b) => ({
      providerName: b?.provider_name ?? "",
      completionModel: b?.completion_model ?? "",
      inheritOrgCredentials: b?.inherit_org_credentials ?? false,
      credentials: "",
    }),
    validate: (d) => {
      if (!d.providerName) return "Pick a provider.";
      if (d.credentials.trim()) {
        try {
          parseStringMap(d.credentials);
        } catch (e: unknown) {
          return e instanceof Error ? e.message : String(e);
        }
      }
      return null;
    },
    submit: (d) =>
      setRealmLlmBinding(realmId, {
        provider_name: d.providerName,
        completion_model: d.completionModel.trim() || null,
        inherit_org_credentials: d.inheritOrgCredentials,
        // Only ever sent by a platform-admin; the field does not render otherwise,
        // so a body the server would 403 on cannot be constructed here.
        ...(byokAllowed && d.credentials.trim()
          ? { credentials: credentialsOrUndefined(d.credentials) }
          : {}),
      }),
    deps: [realmId],
  });

  const picked = catalog.find((p) => p.name === s.draft.providerName);

  return (
    <SectionCard
      title="LLM provider"
      description="Realm binding (D-LLM-1). Applied without a restart — the router re-warms on write."
      footer={
        <SaveBar
          dirty={s.dirty}
          busy={s.busy}
          error={s.fieldError || s.error}
          errorKind={s.fieldError ? "validation" : s.errorKind}
          savedAt={s.savedAt}
          onSave={s.save}
          onReset={s.reset}
        />
      }
    >
      {s.loading ? (
        <div className="text-xs text-kw-text-faint">Loading…</div>
      ) : (
        <>
          {!s.loaded && (
            <div className="text-xs text-amber-400">
              No provider bound. L2 extraction stays queued until one is set (D-LLM-1).
            </div>
          )}
          <Field label="Provider">
            <Select
              value={s.draft.providerName}
              onChange={(v) => s.patch({ providerName: v })}
              options={providerOptions(catalog)}
              placeholder="— pick a catalog provider —"
            />
          </Field>
          <Field
            label="Completion model"
            hint={
              picked?.default_completion_model
                ? `Empty ⇒ the provider default (${picked.default_completion_model}).`
                : "Empty ⇒ the provider default."
            }
          >
            <TextInput
              value={s.draft.completionModel}
              onChange={(v) => s.patch({ completionModel: v })}
              placeholder={picked?.default_completion_model ?? ""}
              mono
            />
          </Field>
          <Toggle
            checked={s.draft.inheritOrgCredentials}
            onChange={(v) => s.patch({ inheritOrgCredentials: v })}
            label="Inherit the organization's credentials"
          />
          <ScopeGate require={canByok} requires="platform-admin">
            <Field
              label="BYOK credentials"
              hint={
                s.loaded?.has_own_credentials
                  ? "Own credentials are stored. Leave empty to keep them; provide a bundle to replace."
                  : "Leave empty for a catalog reference (credentials from the deployment secret store)."
              }
            >
              <JsonTextArea
                value={s.draft.credentials}
                onChange={(v) => s.patch({ credentials: v })}
                placeholder={'{"api_key": "..."}'}
              />
            </Field>
          </ScopeGate>
        </>
      )}
    </SectionCard>
  );
}

interface EmbeddingDraft {
  providerName: string;
  model: string;
  credentials: string;
}

function EmbeddingSection({
  realmId,
  catalog,
}: {
  realmId: string;
  catalog: CatalogProvider[];
}) {
  const grants = useGrants();
  const byokAllowed = canByok(grants);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const s = useEditableSection<EmbeddingBinding | null, EmbeddingDraft>({
    load: () => fetchRealmEmbedding(realmId),
    toDraft: (b) => ({
      providerName: b?.provider_name ?? "",
      model: b?.model ?? "",
      credentials: "",
    }),
    validate: (d) => {
      if (!d.providerName) return "Pick a provider.";
      if (!d.model.trim()) return "An embedding model is required.";
      if (d.credentials.trim()) {
        try {
          parseStringMap(d.credentials);
        } catch (e: unknown) {
          return e instanceof Error ? e.message : String(e);
        }
      }
      return null;
    },
    // `dim` is never sent: the lock always stores the install dim, and the
    // request model forbids extra fields.
    submit: (d) =>
      setRealmEmbedding(realmId, {
        provider_name: d.providerName,
        model: d.model.trim(),
        ...(byokAllowed && d.credentials.trim()
          ? { credentials: credentialsOrUndefined(d.credentials) }
          : {}),
      }),
    deps: [realmId],
  });

  async function removeBinding() {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteRealmEmbedding(realmId);
      setConfirmDelete(false);
      s.reload();
    } catch (e: unknown) {
      setDeleteError(describeApiError(e).message);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <SectionCard
      title="Embedding provider"
      description="A same-dimension override only — the install embedding dimension is locked."
      footer={
        <div className="space-y-3">
          <SaveBar
            dirty={s.dirty}
            busy={s.busy}
            error={s.fieldError || s.error}
            errorKind={s.fieldError ? "validation" : s.errorKind}
            savedAt={s.savedAt}
            onSave={s.save}
            onReset={s.reset}
          />
          {s.loaded && !confirmDelete && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="text-xs text-kw-text-muted hover:text-amber-400"
            >
              Remove override
            </button>
          )}
          {confirmDelete && (
            <ConfirmPanel
              title="Remove the embedding override?"
              tone="danger"
              confirmLabel="Remove"
              busy={deleteBusy}
              error={deleteError}
              onConfirm={removeBinding}
              onCancel={() => setConfirmDelete(false)}
            >
              <p>
                The realm falls back to the installation's embedding provider. Existing vectors
                are unaffected — the dimension is identical by construction.
              </p>
            </ConfirmPanel>
          )}
        </div>
      }
    >
      {s.loading ? (
        <div className="text-xs text-kw-text-faint">Loading…</div>
      ) : (
        <>
          {!s.loaded && (
            <div className="text-xs text-kw-text-faint">
              No override — the realm uses the installation's embedding provider.
            </div>
          )}
          <Field label="Provider">
            <Select
              value={s.draft.providerName}
              onChange={(v) => s.patch({ providerName: v })}
              options={providerOptions(catalog)}
              placeholder="— pick a catalog provider —"
            />
          </Field>
          <Field label="Embedding model">
            <TextInput
              value={s.draft.model}
              onChange={(v) => s.patch({ model: v })}
              mono
              placeholder="text-embedding-3-small"
            />
          </Field>
          {s.loaded && (
            <div className="text-xs text-kw-text-faint">
              Stored dimension <span className="font-mono">{s.loaded.dim}</span> — set by the
              installation, not requestable.
            </div>
          )}
          <ScopeGate require={canByok} requires="platform-admin">
            <Field
              label="BYOK credentials"
              hint="Leave empty for a catalog reference. A provider whose embedding credentials do not resolve is rejected with a 400 naming the reason."
            >
              <JsonTextArea
                value={s.draft.credentials}
                onChange={(v) => s.patch({ credentials: v })}
                placeholder={'{"api_key": "..."}'}
              />
            </Field>
          </ScopeGate>
        </>
      )}
    </SectionCard>
  );
}

export default function RealmModelsRoute() {
  const { realmId } = useRealmContext();
  const [catalog, setCatalog] = useState<CatalogProvider[]>([]);
  const [catalogError, setCatalogError] = useState("");

  useEffect(() => {
    let live = true;
    fetchLlmCatalog()
      .then((providers) => {
        if (live) setCatalog(providers);
      })
      .catch((e: unknown) => {
        if (live) setCatalogError(describeApiError(e).message);
      });
    return () => {
      live = false;
    };
  }, []);

  if (!realmId) return null;

  return (
    <div className="space-y-6">
      {catalogError && (
        <div className="text-xs text-amber-400 border border-amber-500/50 rounded p-3">
          Could not load the provider catalog: {catalogError}
        </div>
      )}
      {/* Every binding here reads and writes `/v1/admin/realms/{id}/…`, gated
          server-side by `require_realm_admin_for`. Gating the block on the same
          predicate names the missing privilege once instead of rendering three
          editable forms that each 403 on load and again on save. */}
      <ScopeGate
        require={(g) => canAdminRealm(g, realmId)}
        requires="realm-admin in this realm"
      >
        {/* Two independent sections: a failed embedding save must not block the
            LLM save, and each has its own dirty state. The render profile lives
            in the Styles tab, where its palette and prompt texts are visible. */}
        <div className="space-y-6">
          <LlmSection realmId={realmId} catalog={catalog} />
          <EmbeddingSection realmId={realmId} catalog={catalog} />
        </div>
      </ScopeGate>
    </div>
  );
}
